import { memo } from "react";
import { useDispatch } from "react-redux";

import { Button } from "antd";
import { IS_BOX_PLOT_CHART, IS_CROSS_PLOT_CHART } from "constants/chart.constants";
import { EvaChart } from "constants/charts.enums";
import { useChartsContext } from "contexts/ChartContext";
import {
  BoxPlotDisplayMode,
  resolveBoxPlotDisplayMode
} from "entities/charts/boxPlotDisplayMode";
import { difference } from "lodash";
import {
  addMultipleLegendItemsToFilter,
  filterWellsFromList,
  updateChartByPath
} from "store/features";
import {
  addMultipleLegendItemsToFilter as addMultipleLegendItemsToFacilitiesFilter,
  filterWellsFromList as filterFacilitiesFromList
} from "store/features/filter/facilityFilterSlice";
import styled from "styled-components";

import { useChartDependencies, useChartEntities } from "hooks/charts";

import { EntityKind } from "models/entityKind";

import { FilterMinus } from "components/icons";
import FilterPlusIcon from "components/icons/FilterPlus";

const AddToFilterButton = () => {
  const { id, entityKind } = useChartsContext();
  const dispatch = useDispatch();
  const { lassoBrushList, chartType, apiResponse, jitterPlotToggle, violinPlotToggle } =
    useChartEntities(id);
  const { groupBy } = useChartDependencies(entityKind);
  const property = groupBy?.property;
  // When violin wins the selection is category-based, not well-based. Derive the mode through
  // the shared resolver so this arbitration can't drift from the translator's.
  const isJitterOn =
    resolveBoxPlotDisplayMode({
      jitterActive: jitterPlotToggle?.active,
      violinActive: violinPlotToggle?.active
    }) === BoxPlotDisplayMode.Jitter;

  function inactiveLasso() {
    dispatch(
      updateChartByPath({
        id,
        path: `features.lasso.active`,
        value: false
      })
    );
  }

  const handleAddToFilter = () => {
    const list = [...lassoBrushList];
    if (
      IS_CROSS_PLOT_CHART(chartType) ||
      chartType === EvaChart.Probit ||
      (IS_BOX_PLOT_CHART(chartType) && isJitterOn)
    ) {
      dispatch(
        entityKind === EntityKind.Facility
          ? filterFacilitiesFromList({ wellList: list })
          : filterWellsFromList({ wellList: list })
      );
    } else {
      dispatch(
        entityKind === EntityKind.Facility
          ? addMultipleLegendItemsToFacilitiesFilter(list, false, property)
          : addMultipleLegendItemsToFilter(list, false, property)
      );
    }

    inactiveLasso();
  };

  const handleExcludeFromFilter = () => {
    if (
      IS_CROSS_PLOT_CHART(chartType) ||
      chartType === EvaChart.Probit ||
      (IS_BOX_PLOT_CHART(chartType) && isJitterOn)
    ) {
      const filteredList = difference(apiResponse.uwis, lassoBrushList);
      const list = [...filteredList];
      dispatch(
        entityKind === EntityKind.Facility
          ? filterFacilitiesFromList({ wellList: list })
          : filterWellsFromList({ wellList: list })
      );
    } else {
      dispatch(
        entityKind === EntityKind.Facility
          ? addMultipleLegendItemsToFacilitiesFilter(lassoBrushList, true, property)
          : addMultipleLegendItemsToFilter(lassoBrushList, true, property)
      );
    }
    inactiveLasso();
  };

  return (
    <>
      <ButtonContainer>
        <StyledButton type="text" onClick={handleAddToFilter}>
          <FilterPlusIcon />
          Add to filter
        </StyledButton>
        <StyledButton type="text" onClick={handleExcludeFromFilter}>
          <FilterMinus />
          Exclude from filter
        </StyledButton>
      </ButtonContainer>
    </>
  );
};

export default memo(AddToFilterButton);

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
`;
